<script setup lang="ts">
import * as d3 from "d3";
import Simplebar from "simplebar-vue";
import { computed, onMounted, ref, useTemplateRef } from "vue";
import { useRoute } from "vue-router";

import ParetoDiagram, { type ParetoPoint } from "@/components/datascience/ParetoDiagram.vue";
import FloatingTooltip from "@/components/ui/FloatingTooltip.vue";
import MarkdownWidget from "@/components/ui/MarkdownWidget.vue";
import SkoreSpinner from "@/components/ui/SkoreSpinner.vue";
import type { EstimatorReportComparisonDto, EstimatorReportDto } from "@/dto";
import { useApi } from "@/services/api";
import { smartToFixed } from "@/services/utils";

const { comparisonId } = defineProps<{
  comparisonId: number;
}>();

const api = useApi();
const route = useRoute();
const tenantName = computed(() => route.params.tenantName as string);
const projectName = computed(() => route.params.projectName as string);

const isLoading = ref(true);
const xMetric = ref("predict_time");
const yMetric = ref("");
const hoveredModelId = ref(-1);
const tooltipPosition = ref({ x: -1, y: -1 });
const comparison = ref<EstimatorReportComparisonDto>();
const reports = ref<EstimatorReportDto[]>([]);
const selectedModelsIds = ref<number[]>([]);
const tradeoffExplanations = ref<{ reportId: string; explanation: string }[]>([]);
const paretoDiagram = useTemplateRef<InstanceType<typeof ParetoDiagram>>("paretoDiagram");
let hideTooltipTimeout = -1;
const selectedReportSymbols = d3.symbolsStroke.filter((s) => s !== d3.symbolSquare2);

const metricsNames = computed(() => [
  ...new Set(
    reports.value
      .flatMap((r) => r.metrics)
      .sort((a, b) => (b.position ?? -Infinity) - (a.position ?? -Infinity))
      .map((m) => m.name)
  ),
]);
const metricsVerboseNamesMap = computed(() => {
  const namesMap = new Map<string, string>();
  reports.value
    .flatMap((r) => r.metrics)
    .forEach((m) => {
      namesMap.set(m.name, m.verbose_name);
    });
  return namesMap;
});
const metricsGreaterIsBetterMap = computed(() => {
  const favorabilityMap = new Map<string, boolean>();
  reports.value
    .flatMap((r) => r.metrics)
    .forEach((m) => {
      favorabilityMap.set(m.name, m.greater_is_better ?? true);
    });
  return favorabilityMap;
});
const pointsOnPlot = computed<ParetoPoint[]>(() => {
  let i = 0;
  const getSymbol = (id: number) => {
    if (selectedModelsIds.value.includes(id)) {
      const symbol = selectedReportSymbols[i];
      i = (i + 1) % selectedReportSymbols.length;
      return symbol;
    }
    return undefined;
  };
  return reports.value
    .map((r) => ({
      id: r.id,
      x: getReportMetricByName(r, xMetric.value),
      y: getReportMetricByName(r, yMetric.value),
      hover: false,
      shape: getSymbol(r.id),
    }))
    .filter((p) => !isNaN(p.x) && !isNaN(p.y));
});
const directions = computed(() => ({
  x: metricsGreaterIsBetterMap.value.get(xMetric.value) ?? true,
  y: metricsGreaterIsBetterMap.value.get(yMetric.value) ?? true,
}));
const selectedModels = computed(() =>
  selectedModelsIds.value.map((id) => reports.value.find((r) => r.id === id)!)
);

function getReportMetricByName(report: EstimatorReportDto, metricName: string) {
  const name = metricsVerboseNamesMap.value.get(metricName);
  if (name) {
    const m = report.metrics.find(
      (m) => (m.data_source === "test" || m.data_source === null) && m.name === metricName
    );
    if (m) {
      return m.value;
    }
  }
  return NaN; // 🤷
}

function getFormattedReportMetricByName(report: EstimatorReportDto, metricName: string) {
  const m = getReportMetricByName(report, metricName);
  if (isNaN(m)) {
    return "--"; // 🤷
  }
  return smartToFixed(m);
}

function getExplanation(reportId: number) {
  const explanation = tradeoffExplanations.value.find((e) => e.reportId === reportId.toString());
  return explanation?.explanation ?? "";
}

async function fetchComparison() {
  isLoading.value = true;

  const c = await api.fetchComparison(tenantName.value, projectName.value, comparisonId);
  if (c !== null) {
    comparison.value = c;

    // fetch reports and filter potentially not found
    const results = (
      await Promise.all(
        c.compared_reports.map((id) =>
          api.fetchEstimatorReport(tenantName.value, projectName.value, id)
        )
      )
    ).filter((r) => r !== null);
    reports.value = results;

    // populate selected trade offs
    selectedModelsIds.value = c.best_trade_offs;
    tradeoffExplanations.value = Object.entries(c.best_trade_offs_explanations).map(
      ([reportId, explanation]) => ({
        reportId,
        explanation,
      })
    );

    yMetric.value = metricsNames.value[0];
  }

  // render
  isLoading.value = false;
}

function getHoveredReportName(reportId: number) {
  const r = reports.value.find((r) => r.id == reportId);
  if (r) {
    return r.key;
  }
  return "";
}

function onMouseOverPoint(id: number, x: number, y: number) {
  clearTimeout(hideTooltipTimeout);
  hoveredModelId.value = id;
  tooltipPosition.value = { x, y };
}

function onMouseOut() {
  hideTooltipTimeout = window.setTimeout(() => {
    hoveredModelId.value = -1;
    tooltipPosition.value = { x: -1, y: -1 };
  }, 200);
}

function onMouseHoverReport(id: number) {
  const r = reports.value.find((r) => r.id == id);
  if (r) {
    const x = getReportMetricByName(r, xMetric.value);
    const y = getReportMetricByName(r, yMetric.value);
    if (!isNaN(x) && !isNaN(y)) {
      clearTimeout(hideTooltipTimeout);
      hoveredModelId.value = id;
      if (paretoDiagram.value && id !== -1) {
        tooltipPosition.value = paretoDiagram.value.getCoordinates(id);
      }
    }
  }
}

function getReportSymbol(id: number) {
  const s = pointsOnPlot.value.find((r) => r.id === id)?.shape ?? d3.symbolSquare2;
  return d3.symbol(s, 14 * 14 * 0.5)();
}

onMounted(async () => {
  await fetchComparison();
});
</script>

<template>
  <div class="estimator-report-comparison-presentation-item">
    <div v-if="isLoading" class="loader">
      <SkoreSpinner />
    </div>
    <div v-else class="container">
      <div class="plot">
        <ParetoDiagram
          ref="paretoDiagram"
          :points="pointsOnPlot"
          :directions="directions"
          @mouse-over-point="onMouseOverPoint"
          @mouse-out-point="onMouseOut"
        />
        <Transition name="fade">
          <div
            v-if="hoveredModelId !== -1 && tooltipPosition.x !== -1 && tooltipPosition.y !== -1"
            class="plot-tooltip"
            :style="{
              transform: `translate(${tooltipPosition.x + 20}px, ${tooltipPosition.y - 20}px)`,
            }"
          >
            {{ getHoveredReportName(hoveredModelId) }}
          </div>
        </Transition>
      </div>
      <div class="selected-models">
        <Simplebar class="scroller">
          <div class="selected-models-list">
            <div v-for="model in selectedModels" :key="model.id" class="selected-model-container">
              <div
                class="selected-model"
                :class="{ hover: model.id === hoveredModelId }"
                @mouseover="onMouseHoverReport(model.id)"
                @mouseout="onMouseOut"
              >
                <div class="name-and-score">
                  <div class="name label-s">
                    <svg class="symbol" width="14" height="14" viewBox="0 0 14 14">
                      <path :d="getReportSymbol(model.id) ?? ''"></path>
                    </svg>
                    {{ model.key }}
                  </div>
                  <div class="metrics">
                    <div v-for="metric in [xMetric, yMetric]" :key="metric" class="label-s">
                      <template v-if="isNaN(getReportMetricByName(model, metric))">
                        <FloatingTooltip
                          strategy="absolute"
                          text="This metric has not been calculated, this model will not appear in the plot."
                        >
                          <i class="warning icon icon-warning" />
                          <span>({{ metricsVerboseNamesMap.get(metric) }})</span>
                        </FloatingTooltip>
                      </template>
                      <template v-else>
                        {{ getFormattedReportMetricByName(model, metric) }}
                        <span>({{ metricsVerboseNamesMap.get(metric) }})</span>
                      </template>
                    </div>
                  </div>
                </div>
                <div class="explanation paragraph-s">
                  <div><i class="icon icon-document" /></div>
                  <div class="text">
                    <MarkdownWidget :source="getExplanation(model.id)" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Simplebar>
      </div>
    </div>
  </div>
</template>

<style scoped>
.estimator-report-comparison-presentation-item {
  .loader {
    display: flex;
    justify-content: center;
  }

  .container {
    display: flex;
    height: 40dvh;
    flex-direction: row;
    gap: var(--spacing-16);
  }

  .plot {
    position: relative;
    z-index: 2;
    width: 50%;
    height: 100%;

    .plot-tooltip {
      position: absolute;
      top: 0;
      left: 0;
      width: max-content;
      padding: var(--spacing-6);
      border-radius: var(--radius-xs);
      background-color: var(--color-tooltip-background);
      box-shadow: var(--box-shadow-l);
      color: var(--color-button-text-primary);
      transition: transform var(--animation-duration) var(--animation-easing);
    }
  }

  .selected-models {
    display: flex;
    width: 50%;
    flex-direction: column;

    .scroller {
      height: 100%;
      padding-bottom: var(--spacing-6);
      overflow-x: hidden;
    }

    .selected-models-list {
      display: flex;
      flex-direction: column;
      gap: var(--spacing-16);
    }

    .selected-model {
      display: flex;
      flex-direction: column;
      padding: var(--spacing-12);
      border: var(--stroke-width-m) solid var(--color-stroke-background-primary);
      border-radius: var(--radius-s);
      gap: var(--spacing-16);
      transition: border-color var(--animation-duration) var(--animation-easing);

      &.hover {
        border-color: hsl(29deg 100% 59%);
      }

      .name-and-score {
        display: flex;
        flex-flow: row wrap;
        align-items: center;
        justify-content: space-between;

        .name {
          padding: var(--spacing-4) var(--spacing-6);
          border: var(--stroke-width-m) solid var(--color-stroke-background-primary);
          border-radius: var(--radius-xs);
          font-family: GeistMono, monospace;

          .symbol {
            width: var(--root-font-size);
            height: var(--root-font-size);
            vertical-align: middle;

            path {
              fill: transparent;
              stroke: hwb(31deg 21% 3%);
              stroke-width: 2.03;
              transform: translate(50%, 50%);
            }
          }
        }

        .metrics {
          display: flex;
          gap: var(--spacing-12);

          span {
            color: var(--color-text-secondary);
          }

          .warning {
            padding-right: var(--spacing-4);
            vertical-align: middle;
          }
        }
      }

      .explanation {
        display: flex;
        flex-direction: row;
        align-items: baseline;
        gap: var(--spacing-8);

        .text {
          display: -webkit-box;
          overflow: hidden;
          max-height: calc(var(--root-font-size) * 1.5 * 3);
          -webkit-box-orient: vertical;
          -webkit-line-clamp: 3;
          line-clamp: 3;
        }
      }
    }
  }
}
</style>
